import {MemberRole} from '@hooks';
import {useWorkspaceUseCase} from '@hooks/workspace-usecase';
import {NotificationDrawer} from '@pages/dashboard/notifications/notification-drawer';
import {useNotificationInbox} from '@pages/dashboard/notifications/use-notification-inbox';
import {useStoreContext} from '@store/store-context';
import {authStore} from '@store/stores/auth-store';
import {goalsState} from '@store/stores/goals-state';
import {performanceStore} from '@store/stores/performance-store';
import {storeDashboardRoutesInit} from '@store/stores/store-dashboard-route';
import {storeYouGoals} from '@store/stores/store-you-goals';
import {usersStore} from '@store/stores/users-store';
import {ActivityIcon, JumpIcon, NestedViewIcon} from '@ui/atoms/icons';
import {ChatVectorIcon} from '@ui/atoms/icons/chat-icon';
import {ProfileDropdown} from '@ui/molecules/profile-dropdown';
import {WorkspaceDropdown} from '@ui/molecules/workspace/dropdown';
import {capitalize} from '@utils';
import {action} from 'mobx';
import {observer} from 'mobx-react';
import {usePostHog} from 'posthog-js/react';
import {lazy, ReactNode, useMemo} from 'react';
import {useNavigate} from 'react-router';
import {Dashboards} from './dashboard-routes';
import {useIsRouteActive} from '@utils/route-href';
import {ChartIcon} from '@ui/atoms/icons/chart';
import {useHeaderVacation} from './use-header-vacation';
import {shouldShowOutOfOffice} from '@ui/organisms/header/header.utils';
import {deriveBillingState, isSubscriptionExpired} from '@utils/billing-state';
import {shouldShowFeatureNavigation} from './header-access';

interface INav {
  title: string;
  href: string;
  disabled?: boolean;
  addBottomBorder?: boolean;
  children: INav[];
  icon: ReactNode | null;
  onClick: () => void;
  'data-testid'?: string;
  active: boolean;
  id: string;
}

const Header = lazy(() =>
  import('../ui/organisms/header/header').then((module) => ({
    default: module.Header,
  })),
);

interface IHeaderProps {
  navigations: INav[];
  accessRestricted: boolean;
}
const workspace_ = (auth: any) => ({
  name:
    auth && auth.user && auth.user.workspace && auth.user.workspace.name
      ? auth.user.workspace.name
      : '',
  logo:
    auth && auth.user && auth.user.workspace && auth.user.workspace.avatar
      ? auth.user.workspace.avatar.url
      : '',
  email: '',
  id:
    auth && auth.user && auth.user.workspace && auth.user.workspace.id
      ? auth.user.workspace.id
      : '',
  link: '',
});

const WorkspaceDropdownComponent = observer(
  ({onClose}: {onClose: () => void}) => {
    const {
      authStore: {auth, featureLimitStatus},
    } = useStoreContext();
    const navigate = useNavigate();

    // TODO: Initialize store for this
    const workspace = {
      name: '',
      logo: '',
      email: '',
      id: '',
      link: '',
    };

    const workspaces = [
      {
        name: '',
        logo: '',
        email: '',
        id: '',
        link: '',
      },
    ];

    return (
      <WorkspaceDropdown
        data-testid="workspace-dropdown"
        onLogout={() => action('clicked logout')}
        onLogoutAll={() => action('clicked logout')}
        onOpenModal={() => navigate('/invite-user')}
        workspaces={auth ? [workspace_(auth)] : workspaces} // TODO (Backend): auth.workspaces replaced with  array-scoped auth.workspace because the login response does not return an array of workspaces.
        onClose={onClose}
        workspace={auth ? workspace_(auth) : workspace}
        isAdmin={auth?.user?.role === 'admin'}
        isInviteDisabled={
          featureLimitStatus?.limited &&
          featureLimitStatus?.currentStatus?.seats >=
            featureLimitStatus?.limits?.seats
        }
      />
    );
  },
);

export const createHeader = () => {
  const ProfileDropdownComponent = observer(function ProfileDropdownComponent({
    onClose,
  }: {
    onClose: () => void;
  }) {
    const {clearAuth, auth} = useStoreContext().authStore;
    const {currentUser} = useStoreContext().usersStore;
    const {currentUserGroups} = useStoreContext().usersStore;
    const postHog = usePostHog();
    const reportee = {
      name:
        auth.user && auth.user.reviewer?.firstName
          ? `${auth.user.reviewer.firstName} ${auth.user.reviewer.lastName}`
          : auth.user.reviewer?.email
          ? `${auth.user.reviewer?.email} `
          : '----',
      avatar:
        auth.user && auth.user.reviewer
          ? `${auth.user.reviewer.avatar?.url}`
          : '',
      id: auth.user?.reviewer?.id,
      url: '',
    };
    const dashboardProfile = useMemo(
      () => ({
        name: auth.user.firstName
          ? `${auth.user.firstName} ${auth.user.lastName}`
          : '',
        avatar: auth.user?.avatar?.url ? `${auth.user.avatar.url}` : '',
        url: '',
        id: auth.user ? `${auth.user.id}` : '',
        role: auth.user ? `${auth.user.role}` : '',
      }),
      [auth.user],
    );

    const logout = () => {
      const {clearAuth} = authStore;
      const {clearGoalsState} = goalsState;
      const {clearPerformanceStore} = performanceStore;
      const {clearDashboardRoutes} = storeDashboardRoutesInit;
      const {clearStoreYouGoals} = storeYouGoals;
      const {clearUsers} = usersStore;

      postHog?.reset();
      clearAuth();
      clearGoalsState();
      clearPerformanceStore();
      clearDashboardRoutes();
      clearStoreYouGoals();
      clearUsers();
    };

    return (
      <ProfileDropdown
        data-testid="profile-dropdown"
        onItemClick={() => action('clicked logout')}
        isAdmin={currentUser.role === 'admin'}
        designation={
          currentUser && currentUser.jobTitle
            ? capitalize(currentUser.jobTitle)
            : ''
        }
        groups={currentUserGroups}
        timezone={currentUser?.timezone || auth.user?.timezone}
        user={dashboardProfile}
        reportee={reportee}
        onClose={onClose}
        onLogout={() => clearAuth(logout)}
      />
    );
  });

  const HeaderComponent = observer(
    ({navigations, accessRestricted}: IHeaderProps) => {
      const {auth} = useStoreContext().authStore;
      const {currentUser} = useStoreContext().usersStore;
      const reportedOutOfOffice = Boolean(
        currentUser?.onVacation ?? auth.user?.onVacation,
      );
      const {vacation, isLoading: isVacationLoading} = useHeaderVacation(
        currentUser?.id || auth.user?.id,
        reportedOutOfOffice,
      );
      const isOutOfOffice = shouldShowOutOfOffice({
        userStatus: reportedOutOfOffice,
        hasVacation: Boolean(vacation),
        isLoading: isVacationLoading,
      });

      const dashboardProfile = useMemo(
        () => ({
          avatar: currentUser?.avatar?.url
            ? `${currentUser.avatar.url}`
            : auth.user?.avatar?.url || '',
          name: currentUser.firstName
            ? `${currentUser.firstName} ${currentUser.lastName}`
            : auth.user?.firstName
            ? `${auth.user.firstName} ${auth.user.lastName}`
            : auth.user?.email || '',
          id: currentUser?.id ? `${currentUser.id}` : `${auth.user?.id || ''}`,
          role: currentUser?.role
            ? `${currentUser.role}`
            : `${auth.user?.role || ''}`,
          url: '',
        }),
        [auth.user, currentUser],
      );

      const dashboardWorkspace = useMemo(
        () => ({
          name:
            auth && auth.user && auth.user.workspace && auth.user.workspace.name
              ? auth.user.workspace.name
              : '',
          avatar:
            auth &&
            auth.user &&
            auth.user.workspace &&
            auth.user.workspace.avatar
              ? auth.user.workspace.avatar.url
              : '',
        }),
        [auth],
      );

      const {unreadCount} = useNotificationInbox();

      return (
        <Header
          WorkspaceDropdown={WorkspaceDropdownComponent}
          profile={dashboardProfile}
          navigations={navigations}
          workspace={dashboardWorkspace}
          outOfOffice={{
            active: isOutOfOffice,
            endDate: vacation?.endDate,
          }}
          notificationCount={unreadCount}
          accessRestricted={accessRestricted}
          AlertsDropdown={NotificationDrawer}
          ProfileDropdown={ProfileDropdownComponent}
        />
      );
    },
  );

  return {Header: HeaderComponent};
};

export const createDashboardRoutes = () => {
  const {Header} = createHeader();

  const DashboardsRoutes = observer(() => {
    const {auth} = useStoreContext().authStore;

    const {
      activateDashboardTab,
      tabs,
      activateGoalViewTab,
    } = useStoreContext().storeDashboardRoutes;

    const {updateDateValue} = useStoreContext().analyticsStore;

    const {
      isEngagementActive,
      isActionItemEnabled,
      isPerformanceActive,
      isCheckinsEnabled,
      isGoalEnabled,
      isFeedBackEnabled,
    } = useWorkspaceUseCase();

    const snapshotDashboardEnabled = false;

    const billingState = deriveBillingState(auth?.user?.workspace?.billing);
    const trailHasEnded = billingState === 'trial_expired';
    const paymentRequired = billingState === 'past_due';

    const subscriptionHasEnded = isSubscriptionExpired(
      auth?.user?.workspace?.billing,
    );
    const accessRestricted =
      trailHasEnded || paymentRequired || subscriptionHasEnded;

    const isFreePlanActive =
      auth?.user?.workspace?.billing?.plan === 'free_tier' ||
      trailHasEnded ||
      paymentRequired;

    const dashboardWorkspace = {
      name:
        auth && auth.user && auth.user.workspace && auth.user.workspace.name
          ? auth.user.workspace.name
          : '',
      avatar:
        auth && auth.user && auth.user.workspace && auth.user.workspace.avatar
          ? auth.user.workspace.avatar.url
          : '',
    };

    const {isRouteActive} = useIsRouteActive();
    const isGoalsRouteActive = isRouteActive(`/goals`);
    const isReportingRouteActive = isRouteActive(`/reporting`);
    const isCheckinsRouteActive = isRouteActive(`/checkins`);
    const isFeedbackRouteActive = isRouteActive(`/feedback`);
    const isViewUserCheckinsRouteActive = isRouteActive(`/view-user-checkins`);
    const isHomeRouteActive = isRouteActive(`/home`);

    const navigations = useMemo(() => {
      const nav: INav[] = [];

      if (snapshotDashboardEnabled) {
        nav.push({
          title: 'Dashboard',
          href: '/',
          children: [],
          icon: <ChartIcon />,
          disabled: false,
          onClick: () => {
            activateDashboardTab('home');
          },
          active: isHomeRouteActive,

          id: '1',
        });
      }

      const showPerformanceNavigation = shouldShowFeatureNavigation({
        featureActive: isPerformanceActive,
        freePlanActive: isFreePlanActive,
        accessRestricted,
      });
      const showEngagementNavigation = shouldShowFeatureNavigation({
        featureActive: isEngagementActive,
        freePlanActive: isFreePlanActive,
        accessRestricted,
      });

      if (showPerformanceNavigation && isGoalEnabled) {
        nav.splice(1, 0, {
          title: 'Goals',
          children: [],
          href: '/goals',
          disabled: accessRestricted,
          icon: <NestedViewIcon width={16} height={16} />,
          'data-testid': 'navigation-goals',
          onClick: () => {
            activateGoalViewTab('flat');
            updateDateValue('');
            activateDashboardTab('objective');
          },
          active: isGoalsRouteActive,
          id: '2',
        });
      }

      if (isCheckinsEnabled && showEngagementNavigation) {
        nav.push({
          title: 'Check-ins',
          href: '/checkins',
          disabled: accessRestricted,
          icon: <JumpIcon />,

          children: [],
          onClick: () => {
            activateDashboardTab('checkins');
            authStore.setCalendarView('month');
          },
          active:
            (isCheckinsRouteActive && !isReportingRouteActive) ||
            isViewUserCheckinsRouteActive,
          id: '3',
        });
      }

      if (isFeedBackEnabled && showEngagementNavigation) {
        nav.push({
          title: 'Feedback',
          href: '/feedback/for-you',
          disabled: accessRestricted,
          icon: <ChatVectorIcon />,
          children: [],
          onClick: () => {
            activateDashboardTab('feedback');
          },
          active: isFeedbackRouteActive,
          id: '6',
        });
      }

      if (
        ((auth && auth.user.isReviewer) ||
          auth.user.role === MemberRole.ADMIN.toLowerCase()) &&
        (isGoalEnabled ||
          isCheckinsEnabled ||
          isFeedBackEnabled ||
          isActionItemEnabled)
      ) {
        nav.unshift({
          title: 'Overview',
          href: '/reporting/overview',
          icon: <ActivityIcon />,
          disabled: accessRestricted,
          children: [],
          onClick: () => {
            activateGoalViewTab('flat');
            activateDashboardTab('reporting');
          },
          active: isReportingRouteActive,
          id: '11',
        });
      }

      return nav;
    }, [
      snapshotDashboardEnabled,
      isPerformanceActive,
      isGoalEnabled,
      isCheckinsEnabled,
      isFeedBackEnabled,
      isEngagementActive,
      auth,
      isHomeRouteActive,
      isGoalsRouteActive,
      isCheckinsRouteActive,
      isViewUserCheckinsRouteActive,
      isFeedbackRouteActive,
      isReportingRouteActive,
      activateDashboardTab,
      isFreePlanActive,
      accessRestricted,
      activateGoalViewTab,
      updateDateValue,
      isActionItemEnabled,
    ]);

    return (
      <Dashboards
        navigations={navigations}
        WorkspaceDropdown={WorkspaceDropdownComponent}
        workspace={dashboardWorkspace}
        header={
          <Header
            navigations={navigations}
            accessRestricted={accessRestricted}
          />
        }
      />
    );
  });

  return {
    DashboardsRoutes,
  };
};
